import { SectionLabel, cx } from "../StudioShared";
import { NODE_META } from "./nodeMeta";
import { Trash2 } from "lucide-react";

const inp = "w-full bg-navy border border-white/10 px-3 py-2 text-sm text-white rounded-sm focus:border-gold focus:outline-none";
const Field = ({ label, children }) => (<div><SectionLabel className="mb-1">{label}</SectionLabel>{children}</div>);
const OPERATORS = ["equals", "not_equals", "contains", "empty", "not_empty", "greater_than", "less_than"];
const DELAY_MODES = ["approval", "wait_hours", "payment", "webhook"];
const FORMATS = ["markdown", "html", "pdf", "docx", "json"];

export default function NodeConfigPanel({ node, strategists, departments, missions, onChange, onDelete }) {
  if (!node) return null;
  const type = node.type;
  const meta = NODE_META[type] || {};
  const cfg = node.data?.config || {};
  const set = (patch) => onChange({ ...node, data: { ...node.data, config: { ...cfg, ...patch } } });
  const setLabel = (label) => onChange({ ...node, data: { ...node.data, label } });

  return (
    <div className="space-y-3" data-testid="wf-config-panel">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {meta.icon && <meta.icon size={16} className={cx(meta.accent)} />}
          <span className="text-white font-medium text-sm">{meta.label}</span>
        </div>
        {type !== "start" && type !== "end" && (
          <button onClick={() => onDelete(node.id)} data-testid="wf-node-delete" title="Delete node"
            className="flex items-center gap-1 text-red-300 border border-red-500/30 px-2 py-1 text-xs rounded-sm hover:bg-red-500/10">
            <Trash2 size={12} /> Delete
          </button>
        )}
      </div>

      <Field label="Node label"><input data-testid="wf-cfg-label" value={node.data?.label || ""} onChange={(e) => setLabel(e.target.value)} className={inp} /></Field>

      {(type === "input" || type === "variable") && (
        <>
          <Field label="Variable key"><input data-testid="wf-cfg-key" value={cfg.key || ""} onChange={(e) => set({ key: e.target.value })} className={inp} placeholder="e.g. business_name" /></Field>
          <Field label={type === "input" ? "Default value" : "Value"}>
            <input data-testid="wf-cfg-value" value={type === "input" ? (cfg.default ?? "") : (cfg.value ?? "")}
              onChange={(e) => set(type === "input" ? { default: e.target.value } : { value: e.target.value })} className={inp} />
          </Field>
        </>
      )}

      {type === "ai_strategist" && (
        <>
          <Field label="Strategist">
            <select data-testid="wf-cfg-strategist" value={cfg.strategist_id || ""} className={inp}
              onChange={(e) => { const s = strategists.find((x) => x.id === e.target.value); set({ strategist_id: e.target.value, strategist_name: s?.name || "" }); }}>
              <option value="">— choose strategist —</option>
              {strategists.map((s) => <option key={s.id} value={s.id}>{s.name} ({s.type})</option>)}
            </select>
          </Field>
          <PromptField cfg={cfg} set={set} />
          <OutputField cfg={cfg} set={set} />
        </>
      )}

      {type === "department" && (
        <>
          <Field label="Department">
            <select data-testid="wf-cfg-department" value={cfg.department || ""} onChange={(e) => set({ department: e.target.value })} className={inp}>
              <option value="">— choose department —</option>
              {departments.map((d) => <option key={d.id || d.name} value={d.name}>{d.name}</option>)}
            </select>
          </Field>
          <PromptField cfg={cfg} set={set} />
          <OutputField cfg={cfg} set={set} />
        </>
      )}

      {type === "mission" && (
        <>
          <Field label="Mission">
            <select data-testid="wf-cfg-mission" value={cfg.mission_id || ""} onChange={(e) => set({ mission_id: e.target.value })} className={inp}>
              <option value="">— choose mission —</option>
              {missions.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
            </select>
          </Field>
          <PromptField cfg={cfg} set={set} />
          <OutputField cfg={cfg} set={set} />
        </>
      )}

      {type === "knowledge" && (
        <Field label="Knowledge domain / collection"><input data-testid="wf-cfg-domain" value={cfg.domain || ""} onChange={(e) => set({ domain: e.target.value })} className={inp} /></Field>
      )}

      {type === "condition" && (
        <>
          <Field label="Variable"><input data-testid="wf-cfg-cond-var" value={cfg.variable || ""} onChange={(e) => set({ variable: e.target.value })} className={inp} placeholder="variable key" /></Field>
          <Field label="Operator"><select data-testid="wf-cfg-operator" value={cfg.operator || "equals"} onChange={(e) => set({ operator: e.target.value })} className={inp}>{OPERATORS.map((o) => <option key={o} value={o}>{o}</option>)}</select></Field>
          {!["empty", "not_empty"].includes(cfg.operator) && (
            <Field label="Value"><input data-testid="wf-cfg-cond-value" value={cfg.value ?? ""} onChange={(e) => set({ value: e.target.value })} className={inp} /></Field>
          )}
          <p className="text-[11px] text-slate-500">Connect the green (T) handle for the true branch and red (F) for false.</p>
        </>
      )}

      {type === "decision" && (
        <>
          <Field label="Default branch"><input data-testid="wf-cfg-branch" value={cfg.default_branch || "true"} onChange={(e) => set({ default_branch: e.target.value })} className={inp} placeholder="true / false / yes / no" /></Field>
          <p className="text-[11px] text-slate-500">Set which branch handle (T/F) the decision follows.</p>
        </>
      )}

      {type === "human_approval" && (
        <Field label="Approval instructions"><textarea data-testid="wf-cfg-instructions" rows={3} value={cfg.instructions || ""} onChange={(e) => set({ instructions: e.target.value })} className={inp} /></Field>
      )}

      {type === "delay" && (
        <>
          <Field label="Wait mode"><select data-testid="wf-cfg-delay-mode" value={cfg.mode || "approval"} onChange={(e) => set({ mode: e.target.value })} className={inp}>{DELAY_MODES.map((m) => <option key={m} value={m}>{m}</option>)}</select></Field>
          {cfg.mode === "wait_hours" && <Field label="Hours"><input data-testid="wf-cfg-delay-hours" type="number" value={cfg.hours || ""} onChange={(e) => set({ hours: e.target.value })} className={inp} /></Field>}
          <p className="text-[11px] text-slate-500">Pauses the run until resumed (approval / payment / webhook).</p>
        </>
      )}

      {type === "provider" && (
        <>
          <Field label="Provider"><input data-testid="wf-cfg-provider" value={cfg.provider || "openai"} onChange={(e) => set({ provider: e.target.value })} className={inp} /></Field>
          <Field label="Model"><input data-testid="wf-cfg-model" value={cfg.model || "auto"} onChange={(e) => set({ model: e.target.value })} className={inp} /></Field>
        </>
      )}

      {type === "export" && (
        <>
          <Field label="Format"><select data-testid="wf-cfg-format" value={cfg.format || "markdown"} onChange={(e) => set({ format: e.target.value })} className={inp}>{FORMATS.map((f) => <option key={f} value={f}>{f}</option>)}</select></Field>
          <Field label="Include variables (comma-separated, blank = all)">
            <input data-testid="wf-cfg-include" value={Array.isArray(cfg.include) ? cfg.include.join(", ") : (cfg.include || "")}
              onChange={(e) => set({ include: e.target.value.split(",").map((x) => x.trim()).filter(Boolean) })} className={inp} />
          </Field>
        </>
      )}
    </div>
  );
}

const PromptField = ({ cfg, set }) => (
  <Field label="Prompt (use {{variable}} placeholders)">
    <textarea data-testid="wf-cfg-prompt" rows={4} value={cfg.prompt || ""} onChange={(e) => set({ prompt: e.target.value })}
      className="w-full bg-navy border border-white/10 px-3 py-2 text-sm text-white rounded-sm focus:border-gold focus:outline-none" />
  </Field>
);
const OutputField = ({ cfg, set }) => (
  <Field label="Output variable">
    <input data-testid="wf-cfg-output" value={cfg.output_var || ""} onChange={(e) => set({ output_var: e.target.value })}
      className="w-full bg-navy border border-white/10 px-3 py-2 text-sm text-white rounded-sm focus:border-gold focus:outline-none" placeholder="e.g. proposal" />
  </Field>
);
